/** Full date/time for tooltips (Trodo tables / conversation panel). */
export function formatAbsoluteDateTime(dateString) {
  if (!dateString) return '';
  try {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    });
  } catch {
    return '';
  }
}

/**
 * Relative time for tables: "1 min ago", "5 days ago", "2 months ago" (full words).
 * @param {Date|string} dateInput
 * @returns {string}
 */
export const formatRelativeTimeAgo = (dateInput) => {
  const date = dateInput instanceof Date ? dateInput : new Date(dateInput);
  if (Number.isNaN(date.getTime())) return '—';

  const now = new Date();
  const diffMs = now - date;
  if (diffMs < 0) {
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  }

  const sec = Math.floor(diffMs / 1000);
  const min = Math.floor(sec / 60);
  const hr = Math.floor(min / 60);
  const day = Math.floor(hr / 24);

  if (sec < 60) return 'just now';
  if (min < 60) return min === 1 ? '1 min ago' : `${min} min ago`;
  if (hr < 24) return hr === 1 ? '1 hour ago' : `${hr} hours ago`;
  if (day < 30) return day === 1 ? '1 day ago' : `${day} days ago`;

  const months =
    (now.getFullYear() - date.getFullYear()) * 12 + (now.getMonth() - date.getMonth());
  if (months < 12) return months <= 1 ? '1 month ago' : `${months} months ago`;

  const years = now.getFullYear() - date.getFullYear();
  return years === 1 ? '1 year ago' : `${years} years ago`;
};

export const formatRelativeTime = (date) => {
  const now = new Date();
  const diffMs = now - date;
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSeconds < 60) {
    return 'just now';
  } else if (diffMinutes < 60) {
    return `${diffMinutes}m ago`;
  } else if (diffHours < 24) {
    return `${diffHours}h ago`;
  } else if (diffDays <= 180) {
    return `${diffDays} days ago`;
  } else {
    // For dates more than 180 days ago, show the actual date
    return date.toLocaleDateString('en-US', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  }
};

/**
 * Format a duration in seconds to a human-readable string
 * @param {number} seconds - Duration in seconds
 * @returns {string} Formatted duration string
 */
export const formatDuration = (seconds) => {
  if (!seconds || seconds === 0) return '0s';
  
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
  
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  
  if (hours < 24) {
    return `${hours}h ${minutes}m`;
  }
  
  const days = Math.floor(hours / 24);
  const remainingHours = hours % 24;
  
  if (remainingHours === 0) {
    return `${days}d`;
  }
  
  return `${days}d ${remainingHours}h`;
};

/**
 * Format a date to show time since session start
 * @param {Date} timestamp - The interaction timestamp
 * @param {Date} sessionStart - The session start time
 * @returns {string} Formatted time since session start
 */
export const formatTimeSinceSessionStart = (timestamp, sessionStart) => {
  if (!timestamp || !sessionStart) return 'N/A';
  
  const diffMs = new Date(timestamp) - new Date(sessionStart);
  const diffSeconds = Math.floor(diffMs / 1000);
  
  if (diffSeconds < 60) return `${diffSeconds}s`;
  if (diffSeconds < 3600) return `${Math.floor(diffSeconds / 60)}m ${diffSeconds % 60}s`;
  
  const hours = Math.floor(diffSeconds / 3600);
  const minutes = Math.floor((diffSeconds % 3600) / 60);
  return `${hours}h ${minutes}m`;
};

/**
 * Check if a date is today
 * @param {Date} date - The date to check
 * @returns {boolean} True if the date is today
 */
export const isToday = (date) => {
  const today = new Date();
  return date.toDateString() === today.toDateString();
};

/**
 * Check if a date is yesterday
 * @param {Date} date - The date to check
 * @returns {boolean} True if the date is yesterday
 */
export const isYesterday = (date) => {
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  return date.toDateString() === yesterday.toDateString();
};

/**
 * Get a human-readable date string
 * @param {Date|string} date - The date to format
 * @returns {string} Formatted date string
 */
export const getReadableDate = (date) => {
  const dateObj = new Date(date);
  
  if (isToday(dateObj)) {
    return 'Today';
  } else if (isYesterday(dateObj)) {
    return 'Yesterday';
  } else {
    return dateObj.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }
};

/**
 * Get a short date string
 * @param {Date|string} date - The date to format
 * @returns {string} Short formatted date string
 */
export const getShortDate = (date) => {
  const dateObj = new Date(date);
  return dateObj.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
};

/**
 * Get time string from date
 * @param {Date|string} date - The date to format
 * @returns {string} Time string
 */
export const getTimeString = (date) => {
  const dateObj = new Date(date);
  return dateObj.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
};

/**
 * Convert timeline filter selection to date range for API calls
 * @param {Object} dateRange - The selected date range object
 * @returns {Object} Object with from and to dates in ISO string format
 */
export const getDateRangeFromFilter = (dateRange) => {
  if (!dateRange || !dateRange.value) {
    return { from: '', to: '' };
  }

  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  switch (dateRange.value) {
    case 'today':
      return {
        from: today.toISOString(),
        to: now.toISOString()
      };
    
    case 'yesterday':
      const yesterdayEnd = new Date(yesterday);
      yesterdayEnd.setHours(23, 59, 59, 999);
      return {
        from: yesterday.toISOString(),
        to: yesterdayEnd.toISOString()
      };
    
    case 'last24h':
      const last24h = new Date(now.getTime() - 24 * 60 * 60 * 1000);
      return {
        from: last24h.toISOString(),
        to: now.toISOString()
      };
    
    case 'last7d':
      const last7d = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      return {
        from: last7d.toISOString(),
        to: now.toISOString()
      };
    
    case 'last14d':
      const last14d = new Date(now.getTime() - 14 * 24 * 60 * 60 * 1000);
      return {
        from: last14d.toISOString(),
        to: now.toISOString()
      };
    
    case 'last30d':
      const last30d = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      return {
        from: last30d.toISOString(),
        to: now.toISOString()
      };
    
    case 'last90d':
      const last90d = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
      return {
        from: last90d.toISOString(),
        to: now.toISOString()
      };
    
    case 'last180d':
      const last180d = new Date(now.getTime() - 180 * 24 * 60 * 60 * 1000);
      return {
        from: last180d.toISOString(),
        to: now.toISOString()
      };
    
    case 'thisMonth':
      const thisMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      return {
        from: thisMonth.toISOString(),
        to: now.toISOString()
      };
    
    case 'yearToDate':
      const yearStart = new Date(now.getFullYear(), 0, 1);
      return {
        from: yearStart.toISOString(),
        to: now.toISOString()
      };
    
    case 'customRange':
      if (dateRange.days) {
        const customDays = new Date(now.getTime() - dateRange.days * 24 * 60 * 60 * 1000);
        return {
          from: customDays.toISOString(),
          to: now.toISOString()
        };
      }
      break;
    
    case 'fromCustomDate':
      if (dateRange.fromDate) {
        return {
          from: new Date(dateRange.fromDate).toISOString(),
          to: now.toISOString()
        };
      }
      break;
    
    case 'customFixedRange':
      if (dateRange.fromDate && dateRange.toDate) {
        const toDate = new Date(dateRange.toDate);
        toDate.setHours(23, 59, 59, 999);
        return {
          from: new Date(dateRange.fromDate).toISOString(),
          to: toDate.toISOString()
        };
      }
      break;
    
    case 'allTime':
    default:
      return { from: '', to: '' };
  }

  return { from: '', to: '' };
};

/**
 * Get the default date range (All time)
 * @returns {Object} Default date range object
 */
export const getDefaultDateRange = () => {
  return { label: 'All time', value: 'allTime' };
};
